import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  XeroConnectionWithStatus,
  getOAuthResponseSuccess,
  isXeroOAuthResponse,
  loadXeroConnectionStatus,
  openXeroConnectPopup,
} from '../../services/xeroConnect';

const OnboardingXero: React.FC = () => {
  const navigate = useNavigate();
  const { organisationId, refreshUserOrganisationState } = useAuth();
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState<XeroConnectionWithStatus | null>(null);
  const [hasProcessedMessage, setHasProcessedMessage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadConnectionStatus();
  }, [organisationId]);

  // Handle OAuth popup messages
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (!isXeroOAuthResponse(event)) return;
      if (hasProcessedMessage) return;

      setHasProcessedMessage(true);

      if (getOAuthResponseSuccess(event)) {
        setIsConnecting(false);
        await loadConnectionStatus();
        await refreshUserOrganisationState();

        // Auto-advance to next step after successful connection
        // setTimeout(() => {
        //   navigate('/onboarding/subscription');
        // }, 1000);
      } else {
        setError('Failed to connect Xero. Please try again.');
        setIsConnecting(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [hasProcessedMessage, navigate, refreshUserOrganisationState]);

  const loadConnectionStatus = async () => {
    if (!organisationId) return;

    const connection = await loadXeroConnectionStatus(organisationId);
    setConnectionInfo(connection);
  };

  const handleConnect = () => {
    if (!organisationId) return;

    setIsConnecting(true);
    setHasProcessedMessage(false);
    setError(null);

    const popup = openXeroConnectPopup('/onboarding/xero');

    if (!popup || popup.closed) {
      setError('Please allow popups for this site to connect your Xero organisation.');
      setIsConnecting(false);
      return;
    }

    const checkPopup = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkPopup);
        setTimeout(() => {
          if (isConnecting && !hasProcessedMessage) {
            setError('Connection cancelled or failed. Please try again.');
            setIsConnecting(false);
          }
        }, 500);
      }
    }, 1000);
  };

  const handleSkip = () => {
    navigate('/onboarding/subscription');
  };

  const handleContinue = () => {
    navigate('/onboarding/subscription');
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box mb={4} textAlign="center">
        {/* <img src="/images/xero-logo.svg" alt="Xero" height="60" /> */}
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Connect to Xero
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Connect your Xero account to automatically import transactions, employees, and assets for
          R&D tracking.
        </Typography>
      </Box>

      <Stack spacing={4}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Connection Status */}
        {connectionInfo?.isConnected ? (
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{
              p: 3,
              bgcolor: 'success.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'success.main',
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img src="/images/xero-logo.svg" alt="Xero" style={{ width: 48, height: 48 }} />
            </Box>
            <Box>
              <Typography variant="body1" color="success.main" fontWeight={600} gutterBottom>
                ✓ Connected to Xero
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Successfully connected to <strong>{connectionInfo.xeroTenantName}</strong>
              </Typography>
            </Box>
          </Stack>
        ) : (
          <Box
            sx={{
              p: 3,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: 'background.paper',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img src="/images/xero-logo.svg" alt="Xero" style={{ width: 48, height: 48 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" fontWeight={600} gutterBottom>
                  Xero Accounting
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Import transactions, employees, and assets automatically
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}

        {/* Benefits List */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            What you'll get:
          </Typography>
          <Stack spacing={1} sx={{ pl: 2 }}>
            <Typography variant="body2" color="text.secondary">
              • Automatic transaction imports from your Xero account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Employee and payroll data for R&D calculations
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Asset tracking for equipment and depreciation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Real-time sync to keep your R&D data up to date
            </Typography>
          </Stack>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'right', alignItems: 'center', mt: 6, gap: 2 }}>
          <Button
            variant="text"
            color="inherit"
            onClick={handleSkip}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Skip for now
          </Button>
          {connectionInfo?.isConnected ? (
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={handleContinue}
              sx={{ minWidth: 200 }}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="contained"
              size="large"
              onClick={handleConnect}
              disabled={isConnecting}
              sx={{ minWidth: 200 }}
            >
              {isConnecting ? 'Connecting...' : 'Connect to Xero'}
            </Button>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default OnboardingXero;
